import { useEffect } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, ArrowLeft } from "@phosphor-icons/react";

const COLORS = [
  ["Rouge DOJOVA", "#D90429", "Monogramme, actions principales"],
  ["Or Kaizen", "#F2A900", "Flèche, accents, surlignage"],
  ["Noir", "#0A0A0A", "Fonds sombres, texte sur clair"],
  ["Blanc cassé", "#F8F5F2", "Fonds clairs, texte sur sombre"],
];
const KIT = [
  ["dojova-mark-color.svg", "Monogramme couleur (fond sombre)", "#0A0A0A"],
  ["dojova-mark-color-on-light.svg", "Monogramme couleur (fond clair)", "#F8F5F2"],
  ["classic-mark-color.svg", "Ancien D « classic » (transition)", "#F8F5F2"],
  ["dojova-mark-black.svg", "Monogramme mono noir", "#FFFFFF"],
  ["dojova-mark-white.svg", "Monogramme mono blanc", "#0A0A0A"],
  ["dojova-icon-square.svg", "Icône app (carré)", "#F8F5F2"],
  ["dojova-horizontal-on-dark.svg", "Horizontal · fond sombre", "#0A0A0A"],
  ["dojova-horizontal-on-light.svg", "Horizontal · fond clair", "#F8F5F2"],
  ["dojova-horizontal-mono-black.svg", "Horizontal · mono noir", "#FFFFFF"],
  ["dojova-horizontal-mono-white.svg", "Horizontal · mono blanc", "#0A0A0A"],
  ["dojova-stacked-on-dark.svg", "Empilé · fond sombre", "#0A0A0A"],
  ["dojova-stacked-on-light.svg", "Empilé · fond clair", "#F8F5F2"],
  ["dojova-stacked-mono-black.svg", "Empilé · mono noir", "#FFFFFF"],
  ["dojova-stacked-mono-white.svg", "Empilé · mono blanc", "#0A0A0A"],
];
const EXPLO = [
  ["tatami-d.jpg", "a. Tatami-D", "Le contre du D devient un carré de tatami — lien direct avec le dojo."],
  ["ceinture-kaizen.jpg", "b. Ceinture-Kaizen", "La flèche devient un nœud de ceinture qui monte — progression des grades."],
  ["torii-d.jpg", "c. Torii-D", "La barre du D forme une porte torii — « entrer dans le système »."],
];
const BADGES = [["ecusson-arbitre", "Arbitre"], ["ecusson-juge", "Juge"], ["ecusson-staff", "Staff"], ["ecusson-benevole", "Bénévole"], ["ecusson-coach", "Coach"], ["ecusson-medical", "Médical"]];
const RULES = [
  ["Zone de protection", "Garder autour du logo un espace libre égal à la hauteur du « D »."],
  ["Taille minimale", "Monogramme : 24 px / 8 mm. Horizontal : 120 px / 35 mm de large."],
  ["Fonds", "Couleur sur noir ou blanc cassé. Mono noir sur fonds clairs, mono blanc sur fonds sombres."],
  ["Interdits", "Jamais sur fond rouge · jamais déformé, incliné ou recoloré · jamais d'ombre portée ni de contour ajouté."],
  ["Typographie", "Outfit — capitales larges pour DOJOVA (700), lettres espacées pour KAIZEN SYSTEM (500). Textes courants : Manrope."],
  ["Co-branding", "Le logiciel = DOJOVA — Kaizen System. L'école = Institut Impact Élite Martial. Les deux marques ne fusionnent jamais."],
];

export default function DojovaBrand() {
  useEffect(() => { document.title = "Charte & kit de marque — DOJOVA"; }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F8F5F2] font-manrope" data-testid="dojova-brand-page">
      <div className="max-w-6xl mx-auto px-5 py-10 sm:py-16">
        <Link to="/dojova" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"><ArrowLeft size={16} /> DOJOVA</Link>
        <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.35em] text-[#F2A900]">Identité visuelle · v2 Tatami-D — adoptée le 18 octobre 2026</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold brand-sans mt-2">Charte & kit de marque</h1>
            <p className="mt-4 text-zinc-400 max-w-2xl">Le logo officiel DOJOVA, vectorisé (SVG) et décliné pour l'écran, l'impression et la broderie. À l'usage de l'équipe et des écoles partenaires.</p>
          </div>
          <a href="/brand/kit/dojova-brand-kit.zip" download data-testid="brand-kit-download" className="inline-flex items-center gap-2 rounded-full bg-[#D90429] px-6 py-3.5 font-bold hover:bg-[#b80322]"><DownloadSimple size={20} /> Télécharger le kit complet (.zip)</a>
        </div>

        <section className="mt-14">
          <H2>Logo officiel</H2>
          <div className="grid md:grid-cols-2 gap-4">
            <Tile bg="#0A0A0A" border><img src="/brand/kit/dojova-horizontal-on-dark.svg" alt="DOJOVA" className="w-full max-w-md" /></Tile>
            <Tile bg="#F8F5F2"><img src="/brand/kit/dojova-horizontal-on-light.svg" alt="DOJOVA" className="w-full max-w-md" /></Tile>
          </div>
        </section>

        <section className="mt-14">
          <H2>Couleurs</H2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {COLORS.map(([n, hex, use]) => (
              <div key={hex} className="rounded-2xl overflow-hidden border border-zinc-800" data-testid={`brand-color-${hex.slice(1)}`}>
                <div className="h-24" style={{ background: hex }} />
                <div className="p-4"><div className="font-bold">{n}</div><div className="font-mono text-sm text-[#F2A900]">{hex}</div><div className="text-xs text-zinc-400 mt-1">{use}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <H2>Kit vectoriel (SVG)</H2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {KIT.map(([f, label, bg]) => (
              <a key={f} href={`/brand/kit/${f}`} download className="group rounded-2xl overflow-hidden border border-zinc-800 hover:border-[#F2A900] transition-colors" data-testid={`brand-kit-${f.replace(".svg", "")}`}>
                <div className="h-36 flex items-center justify-center p-6" style={{ background: bg }}><img src={`/brand/kit/${f}`} alt={label} className="max-h-full max-w-full" /></div>
                <div className="p-3 flex items-center justify-between text-sm"><span>{label}</span><DownloadSimple size={16} className="text-zinc-500 group-hover:text-[#F2A900]" /></div>
              </a>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-3">Textes convertis en tracés (aucune police requise). Exports PNG 2000 px inclus dans le .zip.</p>
        </section>

        <section className="mt-14">
          <H2>Règles d'usage</H2>
          <div className="grid md:grid-cols-2 gap-4">
            {RULES.map(([t, d]) => <div key={t} className="rounded-2xl border border-zinc-800 p-5"><div className="font-bold text-[#F2A900]">{t}</div><p className="text-sm text-zinc-300 mt-1">{d}</p></div>)}
          </div>
        </section>

        <section className="mt-14">
          <H2>Écussons officiels — Jeux Martiaux (Tatami-D)</H2>
          <p className="text-sm text-zinc-400 mb-4">Famille d'écussons vectoriels (SVG net, 4 couleurs, sans dégradé) pour la broderie et l'impression : anneau or, filet rouge, Tatami-D au centre, rôle sur bandeau, « DOJOVA · Institut Impact Élite Martial » en arc. Diamètre conseillé : 75–90 mm.</p>
          <div className="flex flex-wrap gap-3 mb-4">
            <a href="/brand/jeux-martiaux/ecussons-tatami-d.zip" download className="rounded-full bg-[#F2A900] text-black px-4 py-2 text-sm font-semibold" data-testid="brand-badges-zip">Télécharger les écussons (SVG + PNG)</a>
            <a href="/brand/jeux-martiaux/ecussons-famille.svg" download className="rounded-full border border-zinc-700 px-4 py-2 text-sm" data-testid="brand-badges-sheet">Planche complète (SVG)</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {BADGES.map(([f, label]) => (
              <a key={f} href={`/brand/jeux-martiaux/${f}.svg`} download className="rounded-2xl overflow-hidden border border-zinc-800 hover:border-[#F2A900] bg-zinc-950" data-testid={`brand-badge-${f}`}>
                <img src={`/brand/jeux-martiaux/${f}.svg`} alt={`Écusson ${label}`} className="w-full p-3" />
                <div className="p-3 text-sm flex items-center justify-between"><span>{label}</span><span className="text-[11px] text-zinc-500">SVG · <a href={`/brand/jeux-martiaux/${f}.png`} download className="underline">PNG 1600 px</a></span></div>
              </a>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="brand-evolution">
          <H2>Pourquoi le Tatami-D (logo officiel depuis le 18 oct. 2026)</H2>
          <p className="text-sm text-zinc-400 mb-4">Le <b className="text-zinc-200">Tatami-D</b> garde tout ce qui était reconnu (D rouge, flèche or, mêmes proportions) et ajoute le seul élément qui manquait : le dojo. Le contre du D devient un carré de tatami. Lisible à 24 px, se brode bien. L'ancien D (« classic ») reste disponible dans le kit pour la transition.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <Tile bg="#0A0A0A" border><img src="/brand/kit/evolution-tatami-d-horizontal-on-dark.svg" alt="Tatami-D" className="w-full max-w-md" /></Tile>
            <Tile bg="#F8F5F2"><img src="/brand/kit/evolution-tatami-d-horizontal-on-light.svg" alt="Tatami-D" className="w-full max-w-md" /></Tile>
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-sm">
            {[["evolution-tatami-d-mark.svg", "Monogramme (SVG)"], ["evolution-tatami-d-mark-on-light.svg", "Monogramme fond clair (SVG)"], ["evolution-tatami-d-horizontal-on-dark.svg", "Horizontal sombre (SVG)"], ["evolution-tatami-d-horizontal-on-light.svg", "Horizontal clair (SVG)"], ["evolution-tatami-d-mark@2000.png", "PNG 2000 px"]].map(([f, l]) => (
              <a key={f} href={`/brand/kit/${f}`} download className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 px-4 py-2 hover:border-[#F2A900]" data-testid={`brand-evo-${f.split(".")[0]}`}><DownloadSimple size={16} /> {l}</a>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <H2>Pistes d'évolution (exploration)</H2>
          <div className="grid md:grid-cols-3 gap-4">
            {EXPLO.map(([f, t, d]) => (
              <div key={f} className="rounded-2xl overflow-hidden border border-zinc-800" data-testid={`brand-explo-${f.replace(".jpg", "")}`}>
                <img src={`/brand/explorations/${f}`} alt={t} className="w-full" />
                <div className="p-4"><div className="font-bold">{t}</div><p className="text-sm text-zinc-400 mt-1">{d}</p></div>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-3">Visuels d'exploration générés pour discussion — le logo officiel reste celui du kit ci-dessus tant qu'une évolution n'est pas choisie.</p>
        </section>
      </div>
    </div>
  );
}

function H2({ children }) { return <h2 className="text-base md:text-lg uppercase tracking-[0.25em] text-zinc-400 mb-5">{children}</h2>; }
function Tile({ bg, border, children }) { return <div className={`rounded-2xl p-8 flex items-center justify-center ${border ? "border border-zinc-800" : ""}`} style={{ background: bg }}>{children}</div>; }
